import { DocumentHostKind } from "./../types";

// Whether this host, on this platform, can hand the add-in the document's bytes via
// getFileAsync(Office.FileType.Compressed) -- the single API that Save and Check in
// depend on.
//
// Keyed on DocumentHostKind, not HostKind: Outlook has no Office.context.document and
// so no getFileAsync of any kind, which is a different thing from "supported nowhere"
// and would be misread as one if it had a row here.
//
// The coverage is uneven and not guessable, which is why it lives in one table rather
// than being re-reasoned in each adapter:
//
//   Word         Windows, Mac, iPad          -- NOT on the web at all
//   Excel        Windows, Mac                -- web offers Pdf only; iPad unsupported
//   PowerPoint   web, Windows, Mac, iPad     -- the only host the browser is enough for
//
// Getting this wrong is not a crash, it is worse: the user waits through a slice-by-
// slice read of a large file and then sees a failure at the end. So each adapter asks
// before it starts.

/**
 * The platform names as Office.PlatformType spells them.
 *
 * Written as string literals rather than Office.PlatformType members ON PURPOSE. This
 * module is reached at import time (an adapter pulls it in, and the adapter is
 * resolved at module load), which is before Office.onReady -- and under Jest there is
 * no Office global at all. Reading enum members here would throw during evaluation.
 * The enum's values ARE these strings, so the comparison is unaffected.
 */
type PlatformName = "PC" | "OfficeOnline" | "Mac" | "iOS" | "Android" | "Universal";

const SUPPORTED: Record<DocumentHostKind, PlatformName[]> = {
  word: ["PC", "Mac", "iOS"],
  excel: ["PC", "Mac"],
  powerpoint: ["PC", "Mac", "iOS", "OfficeOnline"],
};

const APP_NAME: Record<DocumentHostKind, string> = {
  word: "Word",
  excel: "Excel",
  powerpoint: "PowerPoint",
};

/**
 * A sentence explaining why Save cannot run here, or null when it can.
 *
 * An unreadable platform is treated as supported: this gate exists to give a clear
 * message where we know the API is missing, not to block on uncertainty. If we are
 * wrong the save fails with its own error, which is the situation we were in anyway.
 */
export function describeCompressedBytesUnavailable(host: DocumentHostKind): string | null {
  let platform: string | undefined;
  try {
    platform = Office?.context?.platform as unknown as string | undefined;
  } catch {
    return null;
  }

  if (platform === undefined || SUPPORTED[host].includes(platform as PlatformName)) {
    return null;
  }

  const app = APP_NAME[host];
  return platform === "OfficeOnline"
    ? `Saving to Jylo needs the desktop app. Open this document in ${app} on Windows or Mac to save or check in.`
    : `Saving to Jylo is not available in ${app} on this device. Open the document in ${app} on Windows or Mac to save or check in.`;
}
